import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Switch,
} from '@parcel/ui';
import { useState } from 'react';
import { useAuth } from '../../lib/auth.js';
import { createWorkspace } from '../../lib/workspaces.js';

const reservedWorkspaceSlugs = new Set([
  'signup',
  'signin',
  'login',
  'auth',
  'api',
  'settings',
  'projects',
  'accounts',
  'contacts',
  'alerts',
  'workspaces',
  'workspace',
  'onboarding',
  'docs',
  'pricing',
  'privacy',
  'terms',
  'health',
  'spec',
]);

interface WorkspaceSlugValidation {
  valid: boolean;
  message: string | null;
  slug: string;
}

interface WorkspaceDomainValidation {
  valid: boolean;
  message: string | null;
  domain: string;
}

function normalizeWorkspaceSlugInput(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizeWorkspaceDomainInput(value: string): string {
  const withoutProtocol = value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '');
  if (withoutProtocol.includes('@')) return '';
  return (
    withoutProtocol
      .split('/')[0]
      ?.replace(/^www\./, '')
      .replace(/\.$/, '') ?? ''
  );
}

export function getWorkspaceDomainValidation(
  value: string,
  existingDomains: string[],
): WorkspaceDomainValidation {
  const domain = normalizeWorkspaceDomainInput(value);

  if (
    !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
      domain,
    )
  ) {
    return { valid: false, message: 'Enter a valid company domain.', domain };
  }

  if (existingDomains.includes(domain)) {
    return { valid: false, message: 'That domain is already added.', domain };
  }

  return { valid: true, message: null, domain };
}

function getDomainFromEmail(email: string | undefined): string | null {
  const domain = email?.split('@')[1];
  if (!domain) return null;
  const validation = getWorkspaceDomainValidation(domain, []);
  return validation.valid ? validation.domain : null;
}

export function getWorkspaceCreationDomains(
  domainAccess: boolean,
  email: string | undefined,
): string[] {
  if (!domainAccess) return [];
  const domain = getDomainFromEmail(email);
  return domain ? [domain] : [];
}

export function getWorkspaceSlugValidation(value: string): WorkspaceSlugValidation {
  const slug = normalizeWorkspaceSlugInput(value);

  if (!slug) {
    return { valid: false, message: 'Enter a workspace URL slug.', slug };
  }

  if (slug.length < 3 || slug.length > 48) {
    return {
      valid: false,
      message: 'Workspace URL slug must be between 3 and 48 characters.',
      slug,
    };
  }

  if (reservedWorkspaceSlugs.has(slug)) {
    return { valid: false, message: 'That workspace URL slug is reserved.', slug };
  }

  return { valid: true, message: null, slug };
}

export function WorkspaceCreateForm() {
  const { session } = useAuth();
  const defaultDomain = getDomainFromEmail(session?.user.email) ?? 'parcel.dev';
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [domainAccess, setDomainAccess] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const creationDomains = getWorkspaceCreationDomains(domainAccess, session?.user.email);
  const displayedDomains = domainAccess
    ? creationDomains[0]
      ? creationDomains
      : [defaultDomain]
    : [];

  const slugValidation = getWorkspaceSlugValidation(slug);
  const normalizedSlug = slugValidation.slug;
  const showSlugMessage = slugEdited || slug.length > 0;
  const canSubmit =
    name.trim().length > 0 &&
    slugValidation.valid &&
    (!domainAccess || creationDomains.length > 0) &&
    !submitting;

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="space-y-2 text-center">
        <a href="/" className="inline-flex">
          <img
            src="/brand/parcel-logo-black.svg"
            alt="Parcel"
            width="84"
            height="24"
            decoding="async"
            className="dark:hidden"
          />
          <img
            src="/brand/parcel-logo-white.svg"
            alt="Parcel"
            width="84"
            height="24"
            decoding="async"
            className="hidden dark:block"
          />
        </a>
        <div className="space-y-1">
          <h1 className="font-semibold text-2xl tracking-normal">Create your workspace</h1>
          <p className="text-muted-foreground text-sm">
            Set up the shared Parcel space for your team.
          </p>
        </div>
      </div>

      <form
        className="rounded-lg border bg-background p-6 shadow-sm"
        onSubmit={async (event) => {
          event.preventDefault();
          setError(null);
          setSubmitting(true);

          try {
            const workspace = await createWorkspace({
              name: name.trim(),
              slug: normalizedSlug,
              domainAccess,
              domains: creationDomains,
            });
            window.location.assign(`/${encodeURIComponent(workspace.slug)}/welcome`);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Unable to create workspace.');
            setSubmitting(false);
          }
        }}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="workspace-name">Workspace name</FieldLabel>
            <Input
              id="workspace-name"
              value={name}
              placeholder="Acme Development"
              autoComplete="organization"
              onChange={(event) => {
                const nextName = event.target.value;
                setName(nextName);
                if (!slugEdited) {
                  setSlug(normalizeWorkspaceSlugInput(nextName));
                }
              }}
              required
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="workspace-slug">Workspace URL slug</FieldLabel>
            <Input
              id="workspace-slug"
              value={slug}
              placeholder="acme-development"
              autoComplete="off"
              onChange={(event) => {
                setSlugEdited(true);
                setSlug(normalizeWorkspaceSlugInput(event.target.value));
              }}
              required
            />
            <FieldDescription>
              app.parcelengineering.com/{normalizedSlug || 'workspace'}
            </FieldDescription>
            {showSlugMessage && slugValidation.message ? (
              <p className="text-sm text-destructive">{slugValidation.message}</p>
            ) : null}
          </Field>

          <div className="space-y-4 rounded-md border p-4">
            <div className="flex items-start justify-between gap-4">
              <Field className="gap-1">
                <FieldLabel htmlFor="domain-access">Allowed email domains</FieldLabel>
                <FieldDescription>
                  People with these domains can request access to this workspace.
                </FieldDescription>
              </Field>
              <Switch
                id="domain-access"
                checked={domainAccess}
                onCheckedChange={(checked) => setDomainAccess(Boolean(checked))}
                className="mt-1"
              />
            </div>

            {domainAccess ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {displayedDomains.map((domain) => (
                    <Badge key={domain} variant="secondary">
                      {domain}
                    </Badge>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Workspace creation uses the domain from your signed-in email. Additional domains
                  require verification and can be managed later in security settings.
                </p>
                {creationDomains.length === 0 ? (
                  <p className="text-sm text-destructive">
                    Sign in with a company email domain or turn this off.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <Button type="submit" className="w-full" disabled={!canSubmit}>
            {submitting ? 'Creating...' : 'Create workspace'}
          </Button>
        </FieldGroup>
      </form>
    </div>
  );
}
